import { Autocomplete, Box } from "@mui/material";
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import { useFormik } from "formik";
import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Accordion, Col, Form, Row, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import { MecordColors } from "Constants/Color";
import { autocompleteDropdownStyles } from "Constants/Style";
import { fetchCurrencyByDropDown } from "slices/Masters/currency/thunk";
import { fetchWarehouseDropDown } from "slices/Masters/warehosue/thunk";
import {
  fetchApprovePurchaseRequestDropdownBySupplierId,
  fetchApprovePurchaseRequestId,
  fetchSupplierProductsByPRIDS,
} from "slices/Procurement/purchase-order/approved-pr/thunk";
import { fetchPurchaseRequestId } from "slices/Procurement/purchase-request/thunk";
import AddTagsForPurchaseOrder from "../../Components/AddTagsForPurchaseOrder";
import { fetchOrganizationDropdown } from "slices/UserManagement/organisation/thunk";
import * as Yup from "yup";
import SupplierDetails from "./Components/SupplierDetails";
import ProductDetailsTable from "./Components/ProductDetailsTable";
import { fetchBranchDropdownBySupplierID } from "slices/Masters/supplier/thunk";
import { showCustomSwal } from "helpers/swalHelper";
import { addPurchaseOrder } from "slices/Procurement/purchase-order/purchase-order/thunk";
import LoadingSpinner from "components/LoadingSpinner";
import { ChevronDown, ChevronUp } from "lucide-react";
import FileUpload from "Common/Components/FileUpload.tsx/FileUpload";
import TermsAndConditions from "Common/Components/TermsAndConditions/TermsAndConditions";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";

interface TermsAndConditionItem {
  type: string;
  options: string;
  optionId?: string | number;
}

interface FormValues {
  prs: any;
  po_date: string;
  eta_date: string;
  supplier_id: string;
  supplier_branch_id: string;
  ship_to: any;
  bill_to: any;
  initiated_by: string;
  currency: any;
  exchange_rate: string;
  notes: string;
  terms_and_conditions: TermsAndConditionItem[];
  attachments: (File | string)[];
  tags: string[];
}

const CreatePurchaseOrder: React.FC = () => {
  const { pr_id, po_id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const { state } = useLocation();
  const isEdit = Boolean(po_id);

  const [pr_data, set_pr_data] = useState<any>(null);
  const [pr_supplier_data, set_pr_supplier_data] = useState<any>(null);
  const [shipToOptions, setShipToOptions] = useState<any[]>([]);
  const [billToOptions, setBillToOptions] = useState<any[]>([]);
  const [purchaseRequestOptions, setPurchaseRequestOptions] = useState<any[]>(
    [],
  );

  const [branchOptions, setBranchOptions] = useState<any[]>([]);
  const [currencyOptions, setCurrencyOptions] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [currencyLoading, setCurrencyLoading] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<
    File | File[] | null | string | string[] | (File | string)[]
  >(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [isNotesOpen, setIsNotesOpen] = useState(true);
  const [istermAndConditionOpen, setIsTermsAndConditionOpen] = useState(true);
  const [existingDocuments, setExistingDocuments] = useState<any>([]);
  // Tags the user added manually (not from PRs); preserved when PRs are added/removed
  const customTagsRef = useRef<Set<string>>(new Set());

  // Helper function to recalculate product values with exchange rate
  const recalculateProductsWithExchangeRate = (
    productsArray: any[],
    exchangeRate: number,
  ) => {
    if (!exchangeRate || exchangeRate <= 0) {
      // If exchange rate is invalid, reset to initial state (using unit_price directly)
      return productsArray.map((prod: any) => {
        const originalUnitPrice = Number(prod?.unit_price) || 0;
        const orderQuantity = Number(prod?.order_quantity) || 0;
        const gstPercent = Number(prod?.gst_percent) || 0;

        // Calculate without exchange rate (using unit_price directly)
        const itemAmount = orderQuantity * originalUnitPrice;
        const discountAmount = Number(prod?.discount_amount) || 0;
        const netTaxableValue = itemAmount - discountAmount;
        const gstAmount = (netTaxableValue * gstPercent) / 100;
        const total = netTaxableValue + gstAmount;

        const roundToTwo = (num: number) => Math.round(num * 100) / 100;

        return {
          ...prod,
          rate_in_currency: 0,
          item_amount: roundToTwo(itemAmount),
          net_taxable_value: roundToTwo(netTaxableValue),
          total: roundToTwo(total),
          gst_amount: roundToTwo(gstAmount),
        };
      });
    }

    return productsArray.map((prod: any) => {
      // Get original unit_price (preserve it)
      const originalUnitPrice = Number(prod?.unit_price) || 0;
      const orderQuantity = Number(prod?.order_quantity) || 0;
      const gstPercent = Number(prod?.gst_percent) || 0;

      // Calculate rate in currency: unit_price / exchange_rate
      const rateInCurrency = originalUnitPrice / exchangeRate;

      // Calculate gross value (item amount) = order_quantity * rate_in_currency
      const itemAmount = orderQuantity * rateInCurrency;

      // Calculate net taxable value (gross value - discount if any)
      const discountAmount = Number(prod?.discount_amount) || 0;
      const netTaxableValue = itemAmount - discountAmount;

      // Calculate GST amount
      const gstAmount = (netTaxableValue * gstPercent) / 100;

      // Calculate total = net_taxable_value + gst_amount
      const total = netTaxableValue + gstAmount;

      // Round to 2 decimal places
      const roundToTwo = (num: number) => Math.round(num * 100) / 100;

      return {
        ...prod,
        rate_in_currency: roundToTwo(rateInCurrency),
        item_amount: roundToTwo(itemAmount),
        net_taxable_value: roundToTwo(netTaxableValue),
        total: roundToTwo(total),
        gst_amount: roundToTwo(gstAmount),
      };
    });
  };

  // Helper function to calculate totals from products
  const calculateTotals = (productsArray: any[]) => {
    if (!productsArray || productsArray.length === 0) {
      return {
        subtotal: 0,
        totalGst: 0,
        grandTotal: 0,
      };
    }

    const subtotal = productsArray.reduce((sum, prod) => {
      const netTaxableValue = Number(prod?.net_taxable_value) || 0;
      return sum + netTaxableValue;
    }, 0);

    const totalGst = productsArray.reduce((sum, prod) => {
      const netTaxableValue = Number(prod?.net_taxable_value) || 0;
      const total = Number(prod?.total) || 0;
      // GST amount = total - net_taxable_value
      return sum + (total - netTaxableValue);
    }, 0);

    const grandTotal = productsArray.reduce((sum, prod) => {
      const total = Number(prod?.total) || 0;
      return sum + total;
    }, 0);

    return {
      subtotal,
      totalGst,
      grandTotal,
    };
  };

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      prs: [],
      po_date: new Date().toISOString()?.split("T")?.[0],
      eta_date: "",
      supplier_id: state ? state?.supplier_id : "",
      supplier_branch_id: "",
      ship_to: null,
      bill_to: null,
      initiated_by: "",
      currency: null,
      exchange_rate: "",
      notes: "",
      terms_and_conditions: [],
      attachments: [],
      tags: [],
    },
    validationSchema: Yup.object({
      supplier_branch_id: Yup.string().required(
        "Please select Supplier Branch",
      ),
      prs: Yup.array()
        .min(1, "Please select at least one PR No.")
        .required("Please select at least one PR No."),
      po_date: Yup.string().required("Please select PO Date"),
      initiated_by: Yup.string().required("Intiated by is required"),
      eta_date: Yup.string().optional(),
      ship_to: Yup.object().nullable().required("Please select Ship To"),
      bill_to: Yup.object().nullable().required("Please select Bill To"),
      currency: Yup.object().nullable().required("Currency is required"),
      exchange_rate: Yup.string()
      .required("Exchange Rate is required")
      .test(
        "is-valid-number",
        "Exchange Rate must be a valid positive number",
        (value) => {
          if (!value) return false;
          const regex = /^\d*\.?\d*$/;
          if (!regex.test(value)) return false;
          const numValue = parseFloat(value);
            return !isNaN(numValue) && numValue > 0;
          },
        ),
        supplier_id: Yup.string(),
      }),
    onSubmit: async (values) => {
      if (!products || products.length === 0) {
        toast.error("Add at least one PO item before submitting.");
        return;
      }
      setLoading(true);
      const calculatedTotals = calculateTotals(products);

      // Check if there are files to upload
      // const hasFiles =
      //   attachedFiles &&
      //   ((Array.isArray(attachedFiles) &&
      //     attachedFiles.length > 0 &&
      //     attachedFiles.some((f: any) => f instanceof File)) ||
      //     attachedFiles instanceof File);

      const attachments = formik.values.attachments;

      const hasFiles =
        Array.isArray(attachments) &&
        attachments.some((item): item is File => typeof item !== "string");

      // If files are present, use FormData; otherwise use JSON
      if (hasFiles) {
        const formData = new FormData();

        // Supplier details
        if (pr_supplier_data?.supplier_name)
          formData.append("supplier_name", pr_supplier_data.supplier_name);
        if (pr_supplier_data?.supplier_type)
          formData.append("supplier_type", pr_supplier_data.supplier_type);
        if (pr_supplier_data?.supplier_id)
          formData.append("supplier_id", pr_supplier_data.supplier_id);
        if (pr_supplier_data?.supplier_details)
          formData.append(
            "supplier_details",
            JSON.stringify(pr_supplier_data.supplier_details),
          );
        if (pr_supplier_data?.email)
          formData.append("email", pr_supplier_data.email);
        if (pr_supplier_data?.phone_number)
          formData.append("phone_number", pr_supplier_data.phone_number);
        if (pr_supplier_data?.contact_person_name)
          formData.append(
            "contact_person_name",
            pr_supplier_data.contact_person_name,
          );
        if (pr_supplier_data?.contact_person_email)
          formData.append(
            "contact_person_email",
            pr_supplier_data.contact_person_email,
          );
        if (pr_supplier_data?.contact_person_phone_number)
          formData.append(
            "contact_person_phone_number",
            pr_supplier_data.contact_person_phone_number,
          );
        if (values?.supplier_branch_id)
          formData.append("supplier_branch_id", values.supplier_branch_id);
        formData.append("supplier_branch_details", JSON.stringify({}));

        // PR IDs
        if (values?.prs && Array.isArray(values.prs)) {
          formData.append("pr_ids", JSON.stringify(values.prs));
        }
        formData.append("tags", JSON.stringify(values?.tags ?? []));

        // PO details
        formData.append("po_date", new Date(values?.po_date).toISOString());
        if (values?.initiated_by)
          formData.append("initiated_by", values.initiated_by);
        if (values?.eta_date)
          formData.append("eta_date", new Date(values.eta_date).toISOString());
        if (values?.bill_to?.organization_id)
          formData.append("bill_to_id", values.bill_to.organization_id);
        if (values?.ship_to?.warehouse_id)
          formData.append("ship_to_id", values.ship_to.warehouse_id);
        if (values?.currency?.currency_id)
          formData.append("currency_id", values.currency.currency_id);
        formData.append("exchange_rate", String(values?.exchange_rate));
        if (values?.notes) formData.append("notes", values.notes);
        if (values?.terms_and_conditions?.length > 0)
          formData.append(
            "terms_and_conditions",
            JSON.stringify(values.terms_and_conditions),
          );

        // Totals
        formData.append("subtotal", String(calculatedTotals.subtotal));
        formData.append("total_gst_value", String(calculatedTotals.totalGst));
        formData.append("total", String(calculatedTotals.grandTotal));

        // Products
        formData.append(
          "products",
          JSON.stringify(
            products?.map((prod: any) => ({
              product_id: prod?.product_id || prod?.product_details?.product_id,
              product_name: prod?.product_name,
              product_code: prod?.product_code,
              product_details: prod?.product_details,
              order_quantity: prod?.order_quantity,
              balance_quantity: prod?.order_quantity,
              damaged_quantity: 0,
              quantity: 0,
              unit_name: prod?.unit_name,
              unit_id: prod?.unit_id,
              unit_price: Number(prod?.unit_price),
              rate_in_currency: prod?.rate_in_currency,
              item_amount: Number(prod?.item_amount),
              net_taxable_value: Number(prod?.net_taxable_value),
              gst_percent: Number(prod?.gst_percent),
              igst_percent: Number(prod?.igst_percent),
              cgst_percent: Number(prod?.cgst_percent),
              sgst_percent: Number(prod?.sgst_percent),
              total: Number(prod?.total),
              delivery_lead_time: prod?.delivery_lead_time,
              eta_date: prod?.eta_date || "",
              description: prod?.description,
              remark: prod?.remark,
              pr_product_id: prod?.pr_product_id || null,
              pr_id: prod?.pr_id || null,
            })),
          ),
        );

        // Handle file upload - backend expects single file with field name 'po_document'
        // if (attachedFiles) {
        //   if (Array.isArray(attachedFiles)) {
        //     // If multiple files, take the first File object (not string URL)
        //     const fileToUpload = attachedFiles.find(
        //       (f: any) => f instanceof File,
        //     );
        //     if (fileToUpload) {
        //       formData.append("po_document", fileToUpload);
        //     }
        //   } else if (attachedFiles instanceof File) {
        //     formData.append("po_document", attachedFiles);
        //   }
        // }
        if (values.attachments?.length) {
          values.attachments.forEach((item: any) => {
            const file =
              item instanceof File ? item : item.file || item.originFileObj;

            if (file instanceof File) {
              formData.append("attachments", file); // ✅ NOT attachments[]
            }
          });
        }

        if (existingDocuments.length > 0) {
          formData.append(
            "existingDocuments",
            JSON.stringify(existingDocuments ?? []),
          );
        }

        try {
          await dispatch(addPurchaseOrder(formData)).unwrap();
          toast.success("Purchase Order Created Successfully");
          navigate("/purchase-management/purchase-order");
        } catch (e) {
          handleSubmissionError(e);
        } finally {
          setLoading(false);
        }
      } else {
        // No files, send as JSON
        const payload = {
          // Supplier details
          supplier_name: pr_supplier_data?.supplier_name,
          supplier_type: pr_supplier_data?.supplier_type,
          supplier_id: pr_supplier_data?.supplier_id,
          supplier_details: pr_supplier_data?.supplier_details,
          email: pr_supplier_data.email || null,
          phone_number: pr_supplier_data.phone_number || null,
          contact_person_name: pr_supplier_data.contact_person_name || null,
          contact_person_email: pr_supplier_data.contact_person_email || null,
          contact_person_phone_number:
            pr_supplier_data.contact_person_phone_number || null,
          supplier_branch_id: values?.supplier_branch_id,
          supplier_branch_details: {}, //oprional need to remove
          // ====================
          pr_ids: values?.prs, //for pr reference
          tags: values?.tags ?? [],
          //
          po_date: new Date(values?.po_date),
          initiated_by: values?.initiated_by,
          eta_date: values?.eta_date ? new Date(values.eta_date) : undefined,
          bill_to_id: values?.bill_to?.organization_id,
          ship_to_id: values?.ship_to?.warehouse_id,
          currency_id: values?.currency?.currency_id,
          exchange_rate: Number(values?.exchange_rate),
          notes: values?.notes,
          terms_and_conditions: values?.terms_and_conditions || [],
          subtotal: calculatedTotals.subtotal,
          total_gst_value: calculatedTotals.totalGst,
          total: calculatedTotals.grandTotal,
          products: products?.map((prod: any) => ({
            product_id: prod?.product_id || prod?.product_details?.product_id,
            product_name: prod?.product_name,
            product_code: prod?.product_code,
            product_details: prod?.product_details,
            order_quantity: prod?.order_quantity,
            balance_quantity: prod?.order_quantity,
            damaged_quantity: 0,
            quantity: 0,
            unit_name: prod?.unit_name,
            unit_id: prod?.unit_id,
            unit_price: Number(prod?.unit_price),
            rate_in_currency: prod?.rate_in_currency,
            item_amount: Number(prod?.item_amount),
            net_taxable_value: Number(prod?.net_taxable_value),
            gst_percent: Number(prod?.gst_percent),
            igst_percent: Number(prod?.igst_percent),
            cgst_percent: Number(prod?.cgst_percent),
            sgst_percent: Number(prod?.sgst_percent),
            total: Number(prod?.total),
            delivery_lead_time: prod?.delivery_lead_time,
            eta_date: prod?.eta_date || "",
            description: prod?.description,
            remark: prod?.remark,
            pr_product_id: prod?.pr_product_id || null,
            pr_id: prod?.pr_id || null,
          })),
        };

        try {
          await dispatch(addPurchaseOrder(payload)).unwrap();
          toast.success("Purchase Order Created Successfully");
          navigate("/purchase-management/purchase-order");
        } catch (e) {
          handleSubmissionError(e);
        } finally {
          setLoading(false);
        }
      }

      function handleSubmissionError(err: any) {
        // Handle errors from rejectWithValue (thunk errors)
        // When using unwrap(), the error is the payload directly from rejectWithValue
        // The thunk returns: err.response?.data || "Failed to add PR"

        // If error is a string (from rejectWithValue fallback)
        if (typeof err === "string") {
          toast.error(err);
          return;
        }

        // If error is an object (from err.response?.data)
        if (err && typeof err === "object") {
          const backendMessage = err?.message;
          const errType = err?.errors || {};

          if (errType && Object.keys(errType).length > 0) {
            Object.keys(errType).forEach((key: string) => {
              // if errors are nested arrays/objects, show first message
              const msg = Array.isArray((errType as any)[key])
                ? (errType as any)[key][0]
                : JSON.stringify((errType as any)[key]);
              showCustomSwal({ icon: "error", text: `${msg}` });
            });
            return;
          }

          if (backendMessage) {
            toast.error(backendMessage);
            return;
          }
        }

        // Handle direct axios errors (fallback - should not happen with unwrap)
        const status = err?.response?.status;
        const axiosMessage = err?.response?.data?.message;
        const axiosErrType = err?.response?.data?.errors || {};

        if (axiosErrType && Object.keys(axiosErrType).length > 0) {
          Object.keys(axiosErrType).forEach((key: string) => {
            const msg = Array.isArray((axiosErrType as any)[key])
              ? (axiosErrType as any)[key][0]
              : JSON.stringify((axiosErrType as any)[key]);
            showCustomSwal({ icon: "error", text: `${msg}` });
          });
          return;
        }

        if (status === 404 && axiosMessage) {
          toast.error(axiosMessage);
        } else {
          toast.error("Something went wrong. Please try again.");
        }
      }
    },
  });
  
  useScrollToFormikError(formik.errors, formik.submitCount, formik.isSubmitting)

  useEffect(() => {
    setPageLoading(true);
    Promise.all([
      dispatch(fetchOrganizationDropdown()).unwrap(),
      dispatch(fetchWarehouseDropDown()).unwrap(),
      dispatch(fetchCurrencyByDropDown()).unwrap(),
    ])
      .then(([bill_to_res, ship_to_res, currency_res]: any[]) => {
        setShipToOptions(ship_to_res?.data || []);
        setBillToOptions(bill_to_res || []);
        const currencies = currency_res?.data || [];
        setCurrencyOptions(currencies);
      })
      .catch((err: any) => {
        console.error("Failed to fetch Dropdown:", err);
        toast.error("failed to fetch dropdowns");
      })
      .finally(() => {
        setPageLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!pr_id) return;

    const fetchPrData = async () => {
      setPageLoading(true);
      try {
        const pr_res = await dispatch(
          fetchApprovePurchaseRequestId(pr_id),
        ).unwrap();

        const prData = pr_res?.data;
        set_pr_data(prData || {});

        const approvedSupplier = prData?.suppliers?.[0];
        set_pr_supplier_data(approvedSupplier || {});

        if (!approvedSupplier?.supplier_id) {
          console.warn("No approved supplier found for PR:", pr_id);
          return;
        }

        const [prDropdownRes, branchOptionsRes] = await Promise.all([
          dispatch(
            fetchApprovePurchaseRequestDropdownBySupplierId(
              approvedSupplier.supplier_id,
            ),
          ).unwrap(),
          dispatch(
            fetchBranchDropdownBySupplierID(approvedSupplier.supplier_id),
          ).unwrap(),
        ]);

        setPurchaseRequestOptions(prDropdownRes?.data || []);
        setBranchOptions(branchOptionsRes?.data || []);

        formik.setFieldValue("prs", [pr_id]);
      } catch (err: any) {
        console.error("Failed to fetch PR data:", err);
        toast.error(err?.message || "Something went wrong");
      } finally {
        setPageLoading(false);
      }
    };

    fetchPrData();
  }, [pr_id, dispatch]);

  // Function to calculate GST values based on supplier type and state comparison
  const calculateGSTValues = (gstPercent: number) => {
    // If bill_to or ship_to is not selected, set all GST to 0
    if (!formik.values.bill_to || !formik.values.ship_to) {
      return {
        cgst_percent: 0,
        sgst_percent: 0,
        igst_percent: 0,
      };
    }

    const supplierType = pr_supplier_data?.supplier_type?.toLowerCase();

    // If supplier type is import, set all GST to 0
    if (supplierType === "import") {
      return {
        cgst_percent: 0,
        sgst_percent: 0,
        igst_percent: 0,
      };
    }

    // If supplier type is local, calculate based on state comparison
    if (supplierType === "local") {
      const billToState = formik.values.bill_to?.org_state;
      const shipToState = formik.values.ship_to?.state;

      if (billToState && shipToState && billToState === shipToState) {
        // Same state: CGST and SGST are half of GST, IGST is 0
        const halfGST = gstPercent / 2;
        return {
          cgst_percent: halfGST,
          sgst_percent: halfGST,
          igst_percent: 0,
        };
      } else {
        // Different state: IGST is GST, CGST and SGST are 0
        return {
          cgst_percent: 0,
          sgst_percent: 0,
          igst_percent: gstPercent,
        };
      }
    }

    // Default case (if supplier type is neither local nor import)
    return {
      cgst_percent: 0,
      sgst_percent: 0,
      igst_percent: 0,
    };
  };

  useEffect(() => {
    const supplierId = pr_supplier_data?.supplier_id || formik?.values?.supplier_id;
    if (formik?.values?.prs?.length > 0 && supplierId) {
      try {
        const fetch_pr_products = async () => {
          const pr_products_res = await dispatch(
            fetchSupplierProductsByPRIDS({
              pr_ids: formik.values.prs,
              supplier_id: supplierId,
            }),
          ).unwrap();

          let merged_product: any = [];
          pr_products_res?.data?.map((pr: any) => {
            merged_product.push(...pr?.supplier_products);
          });

          let updated_product = merged_product?.map((prod: any) => {
            const gstValues = calculateGSTValues(prod?.gst_percent || 0);
            return {
              ...prod,
              order_quantity: prod?.update_quantity,
              rate_in_currency: 0,
              ...gstValues,
              remark: "",
            };
          });
          // Apply exchange rate calculation immediately if exchange rate is available
          const exchangeRateValue = formik.values.exchange_rate;
          if (exchangeRateValue && exchangeRateValue !== "") {
            const exchangeRate = Number(exchangeRateValue);
            if (exchangeRate > 0) {
              updated_product = recalculateProductsWithExchangeRate(
                updated_product,
                exchangeRate,
              );
            }
          }
          setProducts(updated_product || []);
        };
        fetch_pr_products();
      } catch (err: any) {
        console.error("Failed to fetch PR No. dropdown:", err);
        toast.error(err?.message);
      }
    }
  }, [formik.values.prs, formik.values.supplier_id, pr_supplier_data]);

  // Sync tags from selected PRs: merge PR tags with user-added custom tags; when a PR is removed, only that PR's tags go away, custom tags stay
  useEffect(() => {
    const customTags = Array.from(customTagsRef.current);
    const prIds = formik.values.prs;
    if (!Array.isArray(prIds) || prIds.length === 0) {
      formik.setFieldValue("tags", customTags);
      return;
    }
    Promise.all(
      prIds.map((id: string) =>
        dispatch(fetchPurchaseRequestId(id)).unwrap()
      )
    )
      .then((responses) => {
        const prTags = responses.flatMap((r) =>
          Array.isArray(r?.data?.tags) ? r.data.tags : []
        );
        const mergedTags = Array.from(new Set([...prTags, ...customTags]));
        formik.setFieldValue("tags", mergedTags);
      })
      .catch(() => {});
  }, [formik.values.prs, dispatch]);

  // Recalculate GST values when bill_to or ship_to changes and products are already loaded
  useEffect(() => {
    // Skip recalculation if supplier type is import
    const supplierType = pr_supplier_data?.supplier_type?.toLowerCase();
    if (supplierType === "import") {
      return; // Don't recalculate for import suppliers
    }

    if (
      products.length > 0 &&
      (formik.values.bill_to || formik.values.ship_to)
    ) {
      // Only update GST breakdown values (CGST/SGST/IGST), preserve all other calculations
      const updatedProducts = products.map((prod: any) => {
        // Get updated GST breakdown values (CGST/SGST/IGST) based on bill_to/ship_to
        const gstValues = calculateGSTValues(prod?.gst_percent || 0);

        // Preserve all existing calculations (rate_in_currency, item_amount, net_taxable_value, total, etc.)
        // Only update the GST breakdown values
        return {
          ...prod,
          ...gstValues, // Only update CGST/SGST/IGST values, everything else stays the same
        };
      });
      setProducts(updatedProducts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.bill_to, formik.values.ship_to, pr_supplier_data]);

  // Recalculate products when exchange rate changes
  useEffect(() => {
    if (products.length > 0) {
      const exchangeRateValue = formik.values.exchange_rate;
      if (exchangeRateValue && exchangeRateValue !== "") {
        const exchangeRate = Number(exchangeRateValue);
        if (exchangeRate > 0) {
          const updatedProducts = recalculateProductsWithExchangeRate(
            products,
            exchangeRate,
          );
          setProducts(updatedProducts);
        } else {
          // Reset to initial state if exchange rate is 0 or invalid
          const resetProducts = recalculateProductsWithExchangeRate(
            products,
            0,
          );
          setProducts(resetProducts);
        }
      } else {
        // Reset to initial state if exchange rate is empty
        const resetProducts = recalculateProductsWithExchangeRate(products, 0);
        setProducts(resetProducts);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.exchange_rate]);

  useEffect(() => {
    if (
      !pr_supplier_data?.supplier_type ||
      !currencyOptions ||
      currencyOptions.length === 0
    ) {
      return;
    }

    const supplierType = pr_supplier_data.supplier_type.toLowerCase();

    if (supplierType === "local") {
      const inrCurrency = currencyOptions.find(
        (c: any) => c.currency_code === "INR" || c.currency_symbol === "₹",
      );

      if (
        inrCurrency &&
        formik.values.currency?.currency_code !== inrCurrency.currency_code
      ) {
        formik.setFieldValue("currency", inrCurrency);
      }

      if (formik.values.exchange_rate !== "1") {
        formik.setFieldValue("exchange_rate", "1");
      }
    }

    if (supplierType === "import") {
      // Clear INR if previously set
      if (
        formik.values.currency?.currency_code === "INR" ||
        formik.values.currency?.currency_symbol === "₹"
      ) {
        formik.setFieldValue("currency", null);
      }

      // Clear exchange rate if auto-set earlier
      // if (formik.values.exchange_rate === "1") {
      //   formik.setFieldValue("exchange_rate", "");
      // }
    }
  }, [
    pr_supplier_data?.supplier_type,
    currencyOptions,
    formik.values.currency,
    formik.values.exchange_rate,
  ]);

  // Calculate totals from products
  const totals = useMemo(() => {
    return calculateTotals(products);
  }, [products]);

  // Format currency with ₹ symbol and comma separators
  const formatCurrency = (amount: number) => {
    return `${amount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  return (
    <Fragment>
      <PageContainer>
        <PageBreadcrumb
          title={isEdit ? "Edit Purchase Order" : "Create Purchase Order"}
          titleLink="/purchase-management/purchase-order"
          breadcrumbs={[
            { label: "Purchase Order", to: "/purchase-management/purchase-order" },
            {
              label: isEdit ? "Edit Purchase Order" : "Create Purchase Order",
              to: "",
            },
          ]}
          backNavigationPath={-1}
        />
        <hr />

        {pageLoading ? (
            <LoadingSpinner />
        ) : (
          <Form autoComplete="off" onSubmit={formik.handleSubmit}>
            <Row>
              <Col lg={6}>
                <div className="mb-4 mt-0">
                  <h6
                    className="fw-semibold mb-2"
                    style={{ fontSize: "0.9rem" }}
                  >
                    Supplier Details
                  </h6>
                  <SupplierDetails
                    formik={formik}
                    pr_data={pr_data}
                    pr_supplier_data={pr_supplier_data}
                    branchOptions={branchOptions}
                  />
                </div>
              </Col>
              <Col lg={6}>
                <h6 className="fw-semibold mb-2" style={{ fontSize: "0.9rem" }}>
                  Purchase Order Details
                </h6>
                <Row
                  style={{
                    backgroundColor: MecordColors?.LightGrayColor,
                    borderRadius: "5px",
                    padding: "10px",
                  }}
                >
                  <Col xs={12} md={12} className="mb-3">
                    <Form.Label className="fw-semibold">
                      PR No. <span className="text-danger">*</span>
                    </Form.Label>
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      options={purchaseRequestOptions}
                      getOptionLabel={(option: any) =>
                        typeof option === "string" ? option : option.pr_no
                      }
                      value={purchaseRequestOptions.filter((pr: any) =>
                        formik.values.prs.includes(pr.pr_id),
                      )}
                      onChange={(event, selected) =>
                        formik.setFieldValue(
                          "prs",
                          selected.map((pr: any) => pr.pr_id),
                        )
                      }
                      onBlur={() => formik.setFieldTouched("prs", true)}
                      renderOption={(props, option, { selected }) => (
                        <li {...props} style={{ padding: 0 }}>
                          <Box
                            sx={{
                              px: 1.5,
                              py: 0.8,
                              width: "100%",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              cursor: "pointer",

                              // ✅ Hover state
                              "&:hover": {
                                backgroundColor: MecordColors.Hover,
                                color: MecordColors.ActiveText,
                              },

                              // ✅ Selected state
                              ...(selected && {
                                backgroundColor: MecordColors.ActiveBackground,
                                color: MecordColors.ActiveText,
                              }),
                            }}
                          >
                            {/* Option Label */}
                            <span>
                              {typeof option === "string"
                                ? option
                                : option.pr_no}
                            </span>

                            {/* Show X only if option is selected */}
                            {selected && (
                              <span
                                style={{
                                  cursor: "pointer",
                                  marginLeft: "10px",
                                  color: "#d32f2f",
                                  fontWeight: "bold",
                                }}
                                onClick={(e) => {
                                  e.stopPropagation(); // prevent normal selection toggling
                                  const newValues = formik.values.prs.filter(
                                    // (pr: any) => pr?.pr_id !== option.pr_id,
                                    (id: any) => id !== option.pr_id
                                  );
                                  formik.setFieldValue("prs", newValues);
                                }}
                              >
                                ✕
                              </span>
                            )}
                          </Box>
                        </li>
                      )}
                      ListboxProps={autocompleteDropdownStyles.ListboxProps}
                      renderInput={(params) => (
                        <BootstrapTextField
                          {...params}
                          id="prs"
                          name="prs"
                          placeholder="Select Purchase Request"
                          error={
                            formik.touched.prs && Boolean(formik.errors.prs)
                          }
                          helperText={
                            formik.touched.prs &&
                            typeof formik.errors.prs === "string"
                              ? formik.errors.prs
                              : ""
                          }
                          fullWidth
                          size="small"
                          sx={{
                            // background: "#FFF",
                            ...autocompleteDropdownStyles.inputSx,
                          }}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option: any, index: number) => {
                          const { key, ...tagProps } = getTagProps({ index });
                          return (
                            <Box
                              key={option.pr_id}
                              component="span"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                backgroundColor: MecordColors.ActiveBackground,
                                borderRadius: "3px",
                                padding: "2px 6px",
                                margin: "1px",
                                fontFamily: "Poppins, sans-serif",
                                fontSize: "13px",
                              }}
                              {...tagProps}
                            >
                              {option.pr_no}
                            </Box>
                          );
                        })
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&:hover fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#ced4da",
                          },
                        },
                      }}
                    />
                  </Col>
                  <Col lg={6} className="mb-3">
                    <Form.Label className="fw-semibold">
                      PO Date<span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      id="po_date"
                      name="po_date"
                      value={formik.values.po_date}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={
                        !!formik.errors.po_date && formik.touched.po_date
                      }
                      style={{ backgroundColor: "#ffffff", color: "#000" }}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.po_date}
                    </Form.Control.Feedback>
                  </Col>

                  <Col lg={6} className="mb-3">
                    <Form.Label className="fw-semibold">
                      Initiated By <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      id="initiated_by"
                      name="initiated_by"
                      placeholder="Enter Name"
                      value={formik.values.initiated_by}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={
                        !!formik.errors.initiated_by &&
                        formik.touched.initiated_by
                      }
                      // style={{ backgroundColor: "#ffffff", color: "#000" }}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.initiated_by}
                    </Form.Control.Feedback>
                  </Col>

                  <Col lg={6} className="mb-3">
                    <Form.Label className="fw-semibold">
                      ETA Date
                    </Form.Label>
                    <Form.Control
                      type="date"
                      id="eta_date"
                      name="eta_date"
                      value={formik.values.eta_date}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={
                        !!formik.errors.eta_date && formik.touched.eta_date
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.eta_date}
                    </Form.Control.Feedback>
                  </Col>
                  <Col xs={12} className="mb-3">
                    <AddTagsForPurchaseOrder
                      label="Tags"
                      placeholder="Enter tags"
                      value={formik.values.tags}
                      onChange={(newTags) => {
                        const prev = formik.values.tags ?? [];
                        const added = newTags.filter((t) => !prev.includes(t));
                        const removed = prev.filter((t) => !newTags.includes(t));
                        added.forEach((t) => customTagsRef.current.add(t));
                        removed.forEach((t) => customTagsRef.current.delete(t));
                        formik.setFieldValue("tags", newTags);
                      }}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>

            <div className="my-4">
              <h6 className="fw-semibold mb-2" style={{ fontSize: "0.9rem" }}>
                Billing & Shipping Details
              </h6>
              <Row>
                <Col lg={6} className="mb-0">
                  <Form.Label className="fw-semibold">
                    Ship To<span className="text-danger">*</span>
                  </Form.Label>
                  <Autocomplete
                    options={shipToOptions}
                    getOptionLabel={(option: any) =>
                      `${option?.warehouse_name} (${option?.address},${option?.city},${option?.state},${option?.country})` ||
                      ""
                    }
                    value={
                      shipToOptions.find(
                        (opt: any) =>
                          opt.warehouse_id ===
                          formik.values.ship_to?.warehouse_id,
                      ) || null
                    }
                    onChange={(e, newValue) => {
                      formik.setFieldValue("ship_to", newValue || "");
                    }}
                    onBlur={() => formik.setFieldTouched("ship_to", true)}
                    renderOption={(props, option, { selected }) => (
                      <li {...props} style={{ padding: 0 }}>
                        <Box
                          sx={{
                            px: 1.5,
                            py: 0.8,
                            width: "100%",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            cursor: "pointer",

                            // ✅ Hover state
                            "&:hover": {
                              backgroundColor: MecordColors.Hover,
                              color: MecordColors.ActiveText,
                            },

                            // ✅ Selected state
                            ...(selected && {
                              backgroundColor: MecordColors.ActiveBackground,
                              color: MecordColors.ActiveText,
                            }),
                          }}
                        >
                          {/* Option Label */}
                          <span>
                            {`${option?.warehouse_name} (${option?.address},${option?.city},${option?.state},${option?.country})` ||
                              ""}
                          </span>
                        </Box>
                      </li>
                    )}
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    renderInput={(params) => (
                      <BootstrapTextField
                        {...params}
                        id="ship_to"
                        name="ship_to"
                        placeholder="Ship To"
                        error={Boolean(
                          formik.touched.ship_to && formik.errors.ship_to,
                        )}
                        helperText={
                          formik.touched.ship_to &&
                          typeof formik.errors.ship_to === "string"
                            ? formik.errors.ship_to
                            : ""
                        }
                        fullWidth
                        size="small"
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                        }}
                      />
                    )}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "#ced4da",
                        },
                        "&:hover fieldset": {
                          borderColor: "#ced4da",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#ced4da",
                        },
                      },
                    }}
                  />
                </Col>

                <Col lg={6} className="mb-0">
                  <Form.Label className="fw-semibold">
                    Bill To<span className="text-danger">*</span>
                  </Form.Label>
                  <Autocomplete
                    options={billToOptions}
                    getOptionLabel={(option: any) =>
                      `${option?.organization_name} (${option?.org_address},${option?.org_city},${option?.org_state},${option?.org_country})` ||
                      ""
                    }
                    value={
                      billToOptions.find(
                        (opt: any) =>
                          opt.organization_id ===
                          formik.values.bill_to?.organization_id,
                      ) || null
                    }
                    onChange={(e, newValue) => {
                      formik.setFieldValue("bill_to", newValue || "");
                    }}
                    onBlur={() => formik.setFieldTouched("bill_to", true)}
                    renderOption={(props, option, { selected }) => (
                      <li {...props} style={{ padding: 0 }}>
                        <Box
                          sx={{
                            px: 1.5,
                            py: 0.8,
                            width: "100%",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            cursor: "pointer",

                            // ✅ Hover state
                            "&:hover": {
                              backgroundColor: MecordColors.Hover,
                              color: MecordColors.ActiveText,
                            },

                            // ✅ Selected state
                            ...(selected && {
                              backgroundColor: MecordColors.ActiveBackground,
                              color: MecordColors.ActiveText,
                            }),
                          }}
                        >
                          {/* Option Label */}
                          <span>
                            {`${option?.organization_name} (${option?.org_address},${option?.org_city},${option?.org_state},${option?.org_country})` ||
                              ""}
                          </span>
                        </Box>
                      </li>
                    )}
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    renderInput={(params) => (
                      <BootstrapTextField
                        {...params}
                        id="bill_to"
                        name="bill_to"
                        placeholder="Bill To"
                        error={Boolean(
                          formik.touched.bill_to && formik.errors.bill_to,
                        )}
                        helperText={
                          formik.touched.bill_to &&
                          typeof formik.errors.bill_to === "string"
                            ? formik.errors.bill_to
                            : ""
                        }
                        fullWidth
                        size="small"
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                        }}
                      />
                    )}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "#ced4da",
                        },
                        "&:hover fieldset": {
                          borderColor: "#ced4da",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#ced4da",
                        },
                      },
                    }}
                  />
                </Col>
              </Row>
            </div>

            <div className="my-4">
              <h6 className="fw-semibold mb-2" style={{ fontSize: "0.9rem" }}>
                Currency & Exchange Details
              </h6>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold">
                      Currency<span className="text-danger">*</span>
                    </Form.Label>
                    <Autocomplete
                      disableClearable
                      disabled={
                        pr_supplier_data?.supplier_type?.toLowerCase() ===
                        "local"
                      }
                      options={currencyOptions}
                      getOptionLabel={(option: any) =>
                        option?.currency_name
                          ? `${option.currency_code} - ${option.currency_name}`
                          : ""
                      }
                      getOptionDisabled={(option: any) => {
                        // Disable ₹ option for import supplier type
                        if (
                          pr_supplier_data?.supplier_type?.toLowerCase() ===
                          "import"
                        ) {
                          return (
                            option?.currency_symbol === "₹" ||
                            option?.currency_code === "INR"
                          );
                        }
                        return false;
                      }}
                      isOptionEqualToValue={(option: any, value: any) => {
                        const optionCode =
                          typeof option === "string"
                            ? option
                            : option?.currency_code;
                        const valueCode =
                          typeof value === "string"
                            ? value
                            : value?.currency_code;
                        return optionCode === valueCode;
                      }}
                      value={
                        formik.values.currency
                          ? (currencyOptions || []).find(
                              (c: any) =>
                                c.currency_code ===
                                formik.values.currency?.currency_code,
                            ) || null
                          : null
                      }
                      onChange={(_: any, newValue: any) => {
                        const currencyCode = newValue?.currency_code || "";
                        const currencySymbol = newValue?.currency_symbol || "";
                        formik.setFieldValue("currency", newValue);
                        if (currencySymbol === "₹") {
                          formik.setFieldValue("exchange_rate", "1");
                        } else {
                          formik.setFieldValue("exchange_rate", "");
                        }
                      }}
                      onBlur={() => {
                        formik.setFieldTouched("currency", true);
                      }}
                      loading={currencyLoading}
                      renderOption={(props, option, { selected }) => (
                        <li {...props} style={{ padding: 0 }}>
                          <Box
                            sx={{
                              px: 1.5,
                              py: 0.8,
                              width: "100%",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              cursor: "pointer",

                              // ✅ Hover state
                              "&:hover": {
                                backgroundColor: MecordColors.Hover,
                                color: MecordColors.ActiveText,
                              },

                              // ✅ Selected state
                              ...(selected && {
                                backgroundColor: MecordColors.ActiveBackground,
                                color: MecordColors.ActiveText,
                              }),
                            }}
                          >
                            {/* Option Label */}
                            <span>
                              {option?.currency_name
                                ? `${option.currency_code} - ${option.currency_name}`
                                : ""}
                            </span>
                          </Box>
                        </li>
                      )}
                      ListboxProps={autocompleteDropdownStyles.ListboxProps}
                      renderInput={(params: any) => (
                        <BootstrapTextField
                          {...params}
                          id="currency"
                          name="currency"
                          placeholder="Select Currency"
                          size="small"
                          error={
                            !!formik.errors.currency && formik.touched.currency
                          }
                          helperText={
                            formik.errors.currency && formik.touched.currency
                              ? formik.errors.currency
                              : ""
                          }
                          fullWidth
                          sx={{
                            ...autocompleteDropdownStyles.inputSx,
                          }}
                        />
                      )}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&:hover fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#ced4da",
                          },
                        },
                      }}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold">
                      Exchange Rate (in INR){" "}
                      <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      id="exchange_rate"
                      name="exchange_rate"
                      placeholder="Enter Exchange Rate"
                      value={formik.values.exchange_rate || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d*\.?\d*$/.test(value)) {
                          formik.setFieldValue("exchange_rate", value);
                        }
                      }}
                      onBlur={formik.handleBlur}
                      disabled={(() => {
                        // For import suppliers, keep exchange rate enabled even if no currency selected
                        const supplierType =
                          pr_supplier_data?.supplier_type?.toLowerCase();
                        if (supplierType === "import") {
                          // Only disable if currency is ₹
                          if (formik.values.currency) {
                            const selectedCurrency = currencyOptions.find(
                              (opt: any) =>
                                opt.currency_code ===
                                formik.values.currency?.currency_code,
                            );
                            return selectedCurrency?.currency_symbol === "₹";
                          }
                          return false; // Enabled for import suppliers even without currency
                        }
                        // For local suppliers or other cases
                        if (
                          !formik.values.currency ||
                          !currencyOptions ||
                          currencyOptions.length === 0
                        ) {
                          return true; // Default to disabled if no currency selected
                        }
                        const selectedCurrency = currencyOptions.find(
                          (opt: any) =>
                            opt.currency_code ===
                            formik.values.currency?.currency_code,
                        );
                        return selectedCurrency?.currency_symbol === "₹";
                      })()}
                      isInvalid={
                        !!formik.errors.exchange_rate &&
                        formik.touched.exchange_rate
                      }
                    />
                    {formik.errors.exchange_rate &&
                      formik.touched.exchange_rate && (
                        <Form.Control.Feedback type="invalid">
                          {formik.errors.exchange_rate}
                        </Form.Control.Feedback>
                      )}
                  </Form.Group>
                </Col>
              </Row>
            </div>

            <ProductDetailsTable products={products} formik={formik} />

            <style>
              {`
                .accordion-notes .accordion-button,
                .accordion-attach-files .accordion-button {
                  background-color: ${MecordColors?.LightGrayColor} !important;
                  color: ${MecordColors?.NormalText} !important;
                }
                .accordion-notes .accordion-button:not(.collapsed),
                .accordion-attach-files .accordion-button:not(.collapsed) {
                  background-color: ${MecordColors?.LightGrayColor} !important;
                  color: ${MecordColors?.NormalText} !important;
                }
              `}
            </style>

            <Row className="mb-4 mt-3">
              <Col md={6}>
                <h6 className="fw-semibold mb-3" style={{ fontSize: "0.9rem" }}>
                  Notes & Attachments
                </h6>
                {/* <Accordion
                  defaultActiveKey="0"
                  className="mb-3 accordion-notes"
                >
                  <Accordion.Item eventKey="0">
                    <Accordion.Header>
                      <span className="fw-semibold">Notes</span>
                    </Accordion.Header>
                    <Accordion.Body>
                      <Form.Group className="mb-3">
                        <Form.Control
                          as="textarea"
                          rows={2}
                          name="notes"
                          placeholder="Enter your business Terms & Conditions"
                          value={formik.values.notes || ""}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                      </Form.Group>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion> */}

                <div
                  className="rounded mb-3 p-2"
                  style={{ backgroundColor: MecordColors?.LightGrayColor }}
                >
                  <div
                    className="d-flex justify-content-start align-items-center p-2 rounded"
                    style={{
                      cursor:
                        !formik.values.terms_and_conditions?.length
                          ? "pointer"
                          : "default",
                      backgroundColor: MecordColors?.LightGrayColor,
                    }}
                    onClick={() => {
                      if (!formik.values.terms_and_conditions?.length) {
                        setIsTermsAndConditionOpen(!istermAndConditionOpen);
                      }
                    }}
                  >
                    {!formik.values.terms_and_conditions?.length && (
                      <div className="me-1">
                        {istermAndConditionOpen ? (
                          <ChevronUp size={20} />
                        ) : (
                          <ChevronDown size={20} />
                        )}
                      </div>
                    )}

                    <h6
                      className="fw-semibold mb-0"
                      style={{ fontSize: "0.9rem" }}
                    >
                      Terms & Condition
                    </h6>
                  </div>

                  {istermAndConditionOpen && (
                    <div className="p-0 mt-2">
                      <TermsAndConditions
                        formik={formik}
                        name="terms_and_conditions"
                        value={formik.values.terms_and_conditions}
                      />
                    </div>
                  )}
                </div>

                <div
                  className="rounded mb-3"
                  style={{ backgroundColor: MecordColors?.LightGrayColor }}
                >
                  {/* Notes Header - Collapsible */}
                  <div
                    className="d-flex justify-content-start align-items-center p-2 rounded"
                    style={{
                      cursor:
                        !formik.values.notes ||
                        formik.values.notes.trim() === ""
                          ? "pointer"
                          : "default",
                      backgroundColor: MecordColors?.LightGrayColor,
                    }}
                    onClick={() => {
                      // Only allow closing if notes are empty
                      if (
                        !formik.values.notes ||
                        formik.values.notes.trim() === ""
                      ) {
                        setIsNotesOpen(!isNotesOpen);
                      }
                    }}
                  >
                    {(!formik.values.notes ||
                      formik.values.notes.trim() === "") && (
                      <div className="me-1">
                        {isNotesOpen ? (
                          <ChevronUp size={20} />
                        ) : (
                          <ChevronDown size={20} />
                        )}
                      </div>
                    )}

                    <h6
                      className="fw-semibold mb-0"
                      style={{ fontSize: "0.9rem" }}
                    >
                      Notes
                    </h6>
                  </div>

                  {/* Notes Content - Collapsible */}
                  {isNotesOpen && (
                    <div className="p-0">
                      <Form.Group>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          name="notes"
                          placeholder="Enter your notes"
                          value={formik.values.notes || ""}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          style={{ maxHeight: "400px", resize: "vertical" }}
                          isInvalid={
                            formik.touched.notes && !!formik.errors.notes
                          }
                        />

                        <Form.Control.Feedback type="invalid">
                          {formik.touched.notes && formik.errors.notes}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </div>
                  )}
                </div>

                <Accordion
                  defaultActiveKey="0"
                  className="mb-3 accordion-attach-files"
                >
                  <Accordion.Item eventKey="0">
                    <Accordion.Header>
                      <span className="fw-semibold">Attach Files</span>
                    </Accordion.Header>
                    <Accordion.Body>
                      <Form.Group className="mb-3">
                        <FileUpload
                          label=""
                          multiple={true}
                          value={formik.values.attachments} // 👈 NEW FILES ONLY
                          existingFiles={existingDocuments} // 👈 EXISTING DOCS
                          onChange={(files) =>
                            formik.setFieldValue("attachments", files)
                          }
                          onRemoveExisting={(index) => {
                            const updatedDocs = [...existingDocuments];
                            updatedDocs.splice(index, 1);
                            setExistingDocuments(updatedDocs);

                            // Optional: track deleted docs for backend
                            // setDeletedDocuments([...deletedDocuments, existingDocuments[index]]);
                          }}
                        />
                      </Form.Group>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </Col>

              <Col md={6}>
                <h6 className="fw-semibold mb-3" style={{ fontSize: "0.9rem" }}>
                  Total Summary
                </h6>
                <div
                  style={{
                    backgroundColor: MecordColors?.LightThemeBGColor,
                    padding: "20px",
                    borderRadius: "8px",
                    border: `1px solid ${MecordColors?.LightThemeBGColor}`,
                  }}
                >
                  <div className="d-flex justify-content-between mb-2">
                    <span>Subtotal</span>
                    <span>
                      {formik?.values?.currency?.currency_symbol}{" "}
                      {formatCurrency(totals.subtotal)}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Total GST Value</span>
                    <span>
                      {formik?.values?.currency?.currency_symbol}{" "}
                      {formatCurrency(totals.totalGst)}
                    </span>
                  </div>
                  <div
                    className="d-flex justify-content-between"
                    style={{
                      borderTop: `1px solid ${MecordColors?.LightThemeBGColor}`,
                      paddingTop: "10px",
                      marginTop: "10px",
                    }}
                  >
                    <span className="fw-bold">Grand Amount</span>
                    <span className="fw-bold">
                      {formik?.values?.currency?.currency_symbol}{" "}
                      {formatCurrency(totals.grandTotal)}
                    </span>
                  </div>
                </div>
              </Col>
            </Row>

            <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
              <CancelButton
                onClick={() => navigate("/purchase-management/purchase-order")}
                redirectTo="/purchase-management/purchase-order"
                disabled={loading || pageLoading}
              />
              <PrimarySubmitButton
                label="Submit"
                isLoading={loading}
                loadingLabel="Submitting..."
                type="submit"
              />
            </div>
          </Form>
        )}
      </PageContainer>
    </Fragment>
  );
};

export default CreatePurchaseOrder;
